import { useEffect, useState, useCallback } from 'react';
import { api, ApiError } from '../api/client';
import './OnePwPanel.css';

interface OnePwStatus {
  total: number;
  eligible: number;
  pushed: number;
}

interface Props {
  orderId: string;
  /** Polling interval in ms while order isn't fully pushed. Default 10s. */
  pollIntervalMs?: number;
}

export function OnePwPanel({ orderId, pollIntervalMs = 10_000 }: Props): JSX.Element {
  const [status, setStatus] = useState<OnePwStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pushing, setPushing] = useState(false);
  const [pushedFlash, setPushedFlash] = useState<number | null>(null);

  const fetchStatus = useCallback(async (): Promise<OnePwStatus | null> => {
    try {
      const data = await api<OnePwStatus>(`/api/orders/${orderId}/onepw-status`);
      setStatus(data);
      setError(null);
      setLoading(false);
      return data;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load 1P status');
      setLoading(false);
      return null;
    }
  }, [orderId]);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  // Keep polling while there's still work outstanding (eligible-not-yet-pushed
  // OR panels still mid-config). Stop once everything that CAN be pushed has been.
  useEffect(() => {
    if (!status) return;
    const incomplete = status.pushed < status.eligible || status.eligible < status.total;
    if (!incomplete) return;
    const handle = setInterval(() => void fetchStatus(), pollIntervalMs);
    return () => clearInterval(handle);
  }, [status, fetchStatus, pollIntervalMs]);

  const handleBulkPush = async (): Promise<void> => {
    setPushing(true);
    try {
      const result = await api<{ queued: number }>(
        `/api/orders/${orderId}/onepw-push-all-done`,
        { method: 'POST' },
      );
      setPushedFlash(result.queued);
      // Force a quick refresh so the operator sees state update ASAP
      // (the worker chews through the queue in seconds for typical batch sizes)
      setTimeout(() => void fetchStatus(), 1500);
      setTimeout(() => setPushedFlash(null), 6000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Bulk push failed');
    } finally {
      setPushing(false);
    }
  };

  if (loading) return <div className="onepw-panel onepw-panel--loading">Loading 1Password status…</div>;
  if (error) return <div className="onepw-panel onepw-panel--error">1Password: {error}</div>;
  if (!status) return <div className="onepw-panel">No 1Password status</div>;

  const ineligible = status.total - status.eligible;
  const remaining = status.eligible - status.pushed;
  const allPushed = status.eligible > 0 && status.pushed >= status.eligible;
  const orderComplete = status.eligible === status.total;

  return (
    <div className={`onepw-panel${allPushed && orderComplete ? ' onepw-panel--complete' : ''}`}>
      <div className="onepw-panel__main">
        <div className="onepw-panel__label">1Password</div>
        <div className="onepw-panel__count">
          <span className="onepw-panel__pushed">{status.pushed}</span>
          <span className="onepw-panel__sep"> / </span>
          <span className="onepw-panel__eligible">{status.eligible}</span>
          <span className="onepw-panel__suffix"> pushed</span>
          {ineligible > 0 && (
            <span className="onepw-panel__ineligible">
              {' '}· {ineligible} not yet eligible
            </span>
          )}
        </div>
        {pushedFlash !== null && (
          <div className="onepw-panel__flash">
            Queued {pushedFlash} push{pushedFlash === 1 ? '' : 'es'} — counter will catch up shortly.
          </div>
        )}
      </div>
      {remaining > 0 && (
        <button
          type="button"
          className="onepw-panel__bulk-btn"
          onClick={() => void handleBulkPush()}
          disabled={pushing}
          title="Enqueue 1P pushes for every fully-configured panel that hasn't been pushed yet"
        >
          {pushing ? 'Queueing…' : `Push all done (${remaining})`}
        </button>
      )}
    </div>
  );
}
